import { FC, useState } from 'react';
import isEqual from 'react-fast-compare';
import Header from '../../../common/Header/Header';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { BuildRequestBodyParams } from '../../../components/TreeView/TreeView.types';
import useTreeViewRowStates from '../../../components/TreeView/useTreeViewRowStates';
import { useGetRecipeBases } from '../../../services/base/base.service';
import { useGetRecipeProducts } from '../../../services/product/product.service';
import { useEditRecipeRawProds } from '../../../services/recipe/recipe.service';
import { useGetRecipeSizes } from '../../../services/size/size.service';
import { fetchSpecsInRecipe, useSpecsInRecipe } from '../../../services/spec/spec.service';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import SelectRawProds from '../AddRawProds/SelectRawProds/SelectRawProds';
import SelectRecipes from '../AddRawProds/SelectRecipes/SelectRecipes';
import Confirmation from './Confirmation/Confirmation';
import ReviewChanges from './ReviewChanges/ReviewChanges';

const ChangeRawProducts: FC = () => {
  const [step, setStep] = useState(0);
  const [currentRawProduct, setCurrentRawProduct] = useState<any[]>([]);
  const [leftRowData, setLeftRowData] = useState<any[]>([]);
  const [centerRowData, setCenterRowData] = useState<any[]>([]);
  const [rightRowData, setRightRowData] = useState<any[]>([]);
  const [selectedRecipes, setSelectedRecipes] = useState<any[]>([]);

  const { mutate, data } = useEditRecipeRawProds();

  const dataStructure = [
    {
      fetchChildren: useGetRecipeBases,
      idKey: 'classNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.row.classNo };
      },
    },
    {
      fetchChildren: useGetRecipeSizes,
      idKey: 'baseNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.parentId, baseNo: params.row.baseNo };
      },
    },
    {
      fetchChildren: useGetRecipeProducts,
      idKey: 'sizeNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      showCheckboxes: true,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentId,
          sizeNo: params.row.sizeNo,
        };
      },
    },
    {
      fetchChildren: useSpecsInRecipe,
      idKey: 'productNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentRows?.[1].id,
          sizeNo: params.parentId,
          productNo: params.row.productNo,
        };
      },
    },
    {
      to: () => '',
      idKey: 'specNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => `${params.row.specNo} - ${params.row.description}`,
    },
  ];

  const { checkedRows, expandedRows, handleRowClick, handleCheckboxClick, handleUpwardsDeselectPropagation } = useTreeViewRowStates(
    dataStructure,
    'selectRecipesToChangeRawProducts',
    true
  );

  const mapParentRowsToFilters = (parentRows: { id: string; level: number }[]) => {
    return {
      classNo: parentRows[0]?.id,
      baseNo: parentRows[1]?.id,
      sizeNo: parentRows[2]?.id,
      productNo: parentRows[3]?.id,
    };
  };

  const handleCheckboxClickInternal = (row: any) => {
    if (row.rowChecked) {
      if (row.level === 4) {
        // recipe row was checked
        setSelectedRecipes((selectedRecipes) => {
          const selectedRecipesWithCheckedRowAdded = [...selectedRecipes, row];
          const onlyUniqueValues = selectedRecipesWithCheckedRowAdded.filter((spec, index) => {
            const firstIndex = selectedRecipesWithCheckedRowAdded.findIndex((val) => val.specNo === spec.specNo);
            return index === firstIndex;
          });
          const newSelectedRecipes = onlyUniqueValues.map((recipe) => {
            return {
              ...recipe,
              rowChecked: true,
              level: 4,
              parentRows: [
                { level: 0, id: recipe.moaClassNo },
                { level: 1, id: recipe.moaBaseNo },
                { level: 2, id: recipe.moaSizeNo },
                { level: 3, id: recipe.moaProductNo },
                { level: 4, id: recipe.specNo },
              ],
            };
          });
          return newSelectedRecipes;
        });
      } else {
        // class / base / size / prod row was checked
        const filters = mapParentRowsToFilters(row.parentRows);
        let parentIndex = -1;
        if (row.level !== 0) {
          const { idKey: parentKey } = dataStructure[row.level - 1] || { idKey: false };

          parentIndex = checkedRows.findIndex(checkedRow => {
            let areParentsEquals = checkedRow.parentRows.every((element: any,  index: number) => {
              return element.id === row.parentRows[index]?.id;
            });
            return checkedRow[parentKey] === row.parentRows[checkedRow.level]?.id && areParentsEquals;
          });
        }
        // fetch the specs that correspond with that row and add them to selectedRecipes (if not already there)
        if (parentIndex === -1) {
          fetchSpecsInRecipe(
            filters,
            (response) => {
              setSelectedRecipes((selectedRecipes) => {
                const selectedRecipesWithResponseDataAdded = [...selectedRecipes, ...response.data];
                const onlyUniqueValues = selectedRecipesWithResponseDataAdded.filter((spec, index) => {
                  const firstIndex = selectedRecipesWithResponseDataAdded.findIndex((val) => val.specNo === spec.specNo);
                  return index === firstIndex;
                });
                const newSelectedRecipes = onlyUniqueValues.map((recipe) => {
                  return {
                    ...recipe,
                    rowChecked: true,
                    level: 4,
                    parentRows: [
                      { level: 0, id: recipe.moaClassNo },
                      { level: 1, id: recipe.moaBaseNo },
                      { level: 2, id: recipe.moaSizeNo },
                      { level: 3, id: recipe.moaProductNo },
                      { level: 4, id: recipe.specNo },
                    ],
                  };
                });
                return newSelectedRecipes;
              });
            },
            (error) => {
              showToast({
                ...genericErrorShowToastOptions,
                label: 'Error fetching specs',
                toastType: ToastTypes.Error,
                id: 'errorFetchingSpecsInRecipe',
              });
            }
          );
        }
      }
    } else {
      // row was just unchecked
      setSelectedRecipes((selectedRecipes) => {
        // filter through selectedRecipes and remove all the recipes that correspond with that row
        return selectedRecipes.filter((recipe) => {
          if (
            !isEqual(
              row.parentRows,
              recipe.parentRows.filter((parentRow: any) => parentRow.level <= row.level)
            )
          ) {
            return true;
          } else {
            return false;
          }
        });
      });
      handleUpwardsDeselectPropagation(row);
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row);
  };

  const renderStep = () => {
    switch (step) {
      default:
        return (
          <SelectRawProds
            flow='currentRawProduct'
            selectedRawProds={currentRawProduct}
            leftRowData={leftRowData}
            centerRowData={centerRowData}
            rightRowData={rightRowData}
            setLeftRowData={setLeftRowData}
            setRightRowData={setRightRowData}
            setCenterRowData={setCenterRowData}
            setSelectedRawProds={setCurrentRawProduct}
            setStep={setStep}
          />
        );
      case 1:
        return (
          <SelectRecipes
          flow='changeRawProds'
          checkedRows={checkedRows}
          expandedRows={expandedRows}
          dataStructure={dataStructure}
          handleRowClick={handleRowClick}
          handleCheckboxClick={handleCheckboxClickInternal}
          selectedRecipes={selectedRecipes}
          setSelectedRecipes={setSelectedRecipes}
          setStep={setStep}
        />
        );
      case 2:
        return (
          <SelectRecipes
            flow='changeRawProds'
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            dataStructure={dataStructure}
            handleRowClick={handleRowClick}
            handleCheckboxClick={handleCheckboxClickInternal}
            selectedRecipes={selectedRecipes}
            setSelectedRecipes={setSelectedRecipes}
            setStep={setStep}
          />
        );
      case 3:
        return (
          <ReviewChanges
            currentRawProduct={centerRowData}
            desiredRawProduct={rightRowData}
            selectedRecipes={selectedRecipes}
            mutate={mutate}
            setStep={setStep}
          />
        );
      case 4:
        return <Confirmation rowData={data?.data || []} />;
    }
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to All Recipes', to: '/recipes' }}
        title='Change Raw Products'
        stepTracker={
          step !== 4
            ? {
                currentStep: step,
                steps: [
                  { label: 'Select Raw Product', onClick: () => setStep(0) },
                  { label: 'Recipes to Update', onClick: () => setStep(1) },
                  { label: 'Review' },
                ],
              }
            : undefined
        }
      />
      {renderStep()}
      <UnsavedChangesModal unsavedChangesExist={step === 1 || step === 2 || step === 3} />
    </>
  );
};

export default ChangeRawProducts;
